import axios from "axios";

// v3 Phase 11 — REACT_APP_WEBSITE points at the findwines-portal origin
// (e.g. https://portal.findwines.ge). We strip a trailing slash so
// callers can use `api.get("/wineries")` regardless of env value.
const portal = (process.env.REACT_APP_WEBSITE || "").replace(/\/+$/, "");

const api = axios.create({
  baseURL: `${portal}/api`,
  timeout: 15000,
});

// Attach JWT (if present) so authenticated endpoints work without each
// call site remembering to set the header.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor:
//   1. Unwrap Laravel's LengthAwarePaginator shape `{current_page, data, …}`
//      to a plain array so call sites don't have to know which endpoints
//      paginate. The frontend currently doesn't render any pagination
//      controls, so the meta is dropped.
//   2. Leave non-paginated responses untouched.
api.interceptors.response.use(
  (response) => {
    const body = response.data;
    if (
      body &&
      typeof body === "object" &&
      Array.isArray(body.data) &&
      // LengthAwarePaginator markers — present together, not just any
      // object with a `data: []` key.
      ("current_page" in body || "last_page" in body || "per_page" in body)
    ) {
      response.data = body.data;
    }
    return response;
  },
  (error) => {
    // Normalize errors so call sites can surface a useful message instead
    // of `error.message` (which is just "Request failed with status 422").
    const status = error.response?.status ?? 0;
    const apiMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      (typeof error.response?.data === "string" ? error.response.data : null);

    let userMessage;
    if (error.code === "ECONNABORTED") {
      userMessage = "Request timed out. Please try again.";
    } else if (status === 0) {
      userMessage = "Cannot reach the server. Check your connection.";
    } else if (status === 401) {
      userMessage = "Please sign in to continue.";
    } else if (status === 403) {
      userMessage = "You don't have permission to do that.";
    } else if (status === 404) {
      userMessage = "Resource not found.";
    } else if (status === 422) {
      userMessage = apiMessage || "Please check the form for errors.";
    } else if (status >= 500) {
      userMessage = "Server error. Please try again in a moment.";
    } else {
      userMessage = apiMessage || error.message;
    }

    error.userMessage = userMessage;
    error.status = status;

    // Always log so the underlying cause is visible in devtools, even
    // when call sites swallow the rejection.
    // eslint-disable-next-line no-console
    console.error("[api]", error.config?.method?.toUpperCase(), error.config?.url, "→", status, apiMessage || error.message);

    return Promise.reject(error);
  }
);

export default api;
